import { GRAY_200, GRAY_800, GRAY_900, MOBILE_VIEW_HEIGHT_QUERY, MOBILE_VIEW_WIDTH_QUERY, TABLET_VIEW_QUERY } from 'constants/styling/theme'
import { IconButton, Stack, Typography } from '@mui/material'
import React, { Fragment, KeyboardEvent, MouseEvent, useCallback, useState } from 'react'

import { APIRequestErrorType } from 'constants/API'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import { Close } from '@mui/icons-material'
import EastRoundedIcon from '@mui/icons-material/EastRounded'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ErrorMessage from 'components/common/ErrorMessage/ErrorMessage'
import { ExpandedVisual } from './ExpandedVisual.component'
import { ExpandedVisualSwitchDirection } from './ExpandedVisualPopupBase.component'
import { ExpandedVisualType } from 'components/pages/Gallery/_main/ClientGallery/ClientGalleryPhoto'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import { KeyboardEventKey } from 'constants/misc'
import { MUIBadge } from 'components/common/MUIBadge'
import { MUIButton } from 'components/common/MUIButton'
import Modal from '../Modals/Modal/Modal'
import { Nullable } from 'models/helpers'
import { PhotoStudioUnsupportedModal } from '../Modals/PhotoStudioUnsupportedModal/PhotoStudioUnsupportedModal'
import { ResponsiveDrawer } from 'components/common/ResponsiveDrawer'
import { VisualClientDTO } from 'models/visuals'
import WestRoundedIcon from '@mui/icons-material/WestRounded'
import styles from './ExpandedVisualPopup.module.sass'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTranslation } from 'react-i18next'

/**
 * @interface Props Input properties
 */
interface Props {
  /** Whether the popup is open */
  isOpen: boolean
  /** Whether the preview image is selected or not */
  isSelected?: boolean
  /** Whether the preview image is selectable or not */
  isSelectable?: boolean
  /** Whether the gallery image can be marked as favorite or not */
  isFavoriteSelectable?: boolean
  /** Whether the gallery image is marked as favorite or not */
  isFavorite?: boolean
  /** Whether the gallery image is purchased or not */
  isPurchased?: boolean
  /** Signed Url for displaying image in popup */
  signedUrl?: string
  /** File name of image */
  filename?: string
  /** Label of image title badge */
  label?: string
  /** OnClick action to switch to previous/next visual */
  switchExpandedVisual: (direction: ExpandedVisualSwitchDirection) => void
  /** OnClick action to switch expanded visual type */
  switchExpandedVisualType?: (type: ExpandedVisualType) => void
  /** Current expanded visual type: original or edited */
  expandedVisualType?: ExpandedVisualType
  /** OnClick action to close the popup */
  onClose: () => void
  /** OnSelect action to be called */
  onSelect?: (e: MouseEvent) => void
  /** OnClick action to download image */
  onDonwload?: () => void
  /** OnClick action to open Photo Studio */
  onOpenPhotoStudio?: () => void
  /** onClick action to toggle favorite image */
  toggleFavoriteOnImage?: (filename?: string) => void
  /** Metadata of a visual */
  visualMetadata?: Nullable<VisualClientDTO>
}

/**
 * TODO: This popup should be an extension of newly created simplified ExpandedVisualPopupBase component, yet i do not posses the time required to do it now
 * @component ExpandedGalleryVisualPopup 
 * @example
 * <ExpandedGalleryVisualPopup isOpen={true} signedUrl={url} filename="photo_01" switchExpandedVisual={() => {}} onClose={()=> {}} />
 */

export const ExpandedGalleryVisualPopup: React.FC<Props> = ({
  isOpen,
  isSelected,
  isSelectable = false,
  isFavoriteSelectable = false,
  isFavorite = false,
  isPurchased = false,
  signedUrl,
  filename,
  label,
  switchExpandedVisual,
  switchExpandedVisualType,
  expandedVisualType,
  onClose,
  onSelect,
  onDonwload,
  onOpenPhotoStudio,
  toggleFavoriteOnImage,
  visualMetadata
}) => {

  const isMobileViewWidth = useMediaQuery(MOBILE_VIEW_WIDTH_QUERY)
  const isMobileViewHeight = useMediaQuery(MOBILE_VIEW_HEIGHT_QUERY)
  const isTabletView = useMediaQuery(TABLET_VIEW_QUERY)
  const { t } = useTranslation(['common', 'actions'])

  const keyDownHandler = useCallback((e: KeyboardEvent) => {
    switch (e.key) {
      case KeyboardEventKey.ESCAPE:
        return onClose()
      case KeyboardEventKey.ARROW_LEFT:
        return switchExpandedVisual(ExpandedVisualSwitchDirection.LEFT)
      case KeyboardEventKey.ARROW_RIGHT:
        return switchExpandedVisual(ExpandedVisualSwitchDirection.RIGHT)
    }
  }, [onClose, switchExpandedVisual])

  let style: React.CSSProperties = {}
  if (signedUrl) {
    style.backgroundImage = `url('${signedUrl}')`
  }

  const [isPhotoStudioUnsupportedModalOpen, setIsPhotoStudioUnsupportedModalOpen] = useState(false)

  const handlePhotoStudioClick = () => {
    if ((isMobileViewWidth && isMobileViewHeight) || isTabletView) {
      setIsPhotoStudioUnsupportedModalOpen(true)
    } else {
      onOpenPhotoStudio?.()
    }
  }

  return (
    <Fragment>
      {(isMobileViewWidth && isMobileViewHeight) || isTabletView ?
        <ResponsiveDrawer
          title={filename || ''}
          isOpen={isOpen}
          onClose={onClose}
          height="70vh"
        >

          <ExpandedVisual
            signedUrl={signedUrl}
            isSelected={isSelected}
            isSelectable={isSelectable}
            isFavorite={isFavorite}
            isFavoriteSelectable={isFavoriteSelectable}
            visualMetadata={visualMetadata}
            switchExpandedVisualType={switchExpandedVisualType}
            expandedVisualType={expandedVisualType}
            onSelect={onSelect}
            onClickFavButton={() => toggleFavoriteOnImage?.(filename)}
          />

          <Stack flexDirection="row" justifyContent="center" alignItems="center" gap=".8rem" marginTop="2.4rem">
            <MUIButton
              type="secondaryBorder"
              variant="outlined"
              size="lg"
              fullWidth
              startIcon={<ArrowBackRoundedIcon fontSize="large" />}
              onClick={() => switchExpandedVisual(ExpandedVisualSwitchDirection.LEFT)}
            >
              {t('Previous')}
            </MUIButton>
            <MUIButton
              type="secondaryBorder"
              variant="outlined"
              size="lg"
              fullWidth
              endIcon={<ArrowForwardRoundedIcon fontSize="large" />}
              onClick={() => switchExpandedVisual(ExpandedVisualSwitchDirection.RIGHT)}
            >
              {t('Next')}
            </MUIButton>
          </Stack>
        </ResponsiveDrawer>
        :
        <Modal
          modalContentClassName={styles.expandedVisualPopup}
          isOpen={isOpen}
          onClickOutside={onClose}
          onKeyDown={keyDownHandler}
          footerContent={
            <Stack flexDirection="row" justifyContent="flex-end" alignItems="center" gap=".8rem" padding="1.2rem 1.6rem">
              {isPurchased ?
                <>
                  <MUIButton
                    size="lg"
                    type="secondaryNoBorder"
                    startIcon={<EditOutlinedIcon sx={{ color: GRAY_800, fontSize: '1.6rem' }} />}
                    onClick={handlePhotoStudioClick}
                  >
                    {t('Photo_Studio')}
                  </MUIButton>

                  <MUIButton
                    size="lg"
                    startIcon={<FileDownloadOutlinedIcon fontSize="large" />}
                    onClick={onDonwload}
                  >
                    {t('Download')}
                  </MUIButton>
                </>
                :
                <MUIButton
                  size="lg"
                  type="secondaryNoBorder"
                  onClick={onClose}
                >
                  {t('actions:Close')}
                </MUIButton>
              }
            </Stack>
          }
        >
          <Stack
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            padding="1.6rem 2.4rem"
          >
            <Stack gap=".8rem" flexDirection="row" alignItems="center">
              <Typography variant="text-xl" fontWeight="semiBold" color={GRAY_900}>{filename}</Typography>
              {!!label && <MUIBadge label={label} size="sm" color="gray" />}
            </Stack>
            <IconButton
              size="small"
              sx={{
                color: GRAY_800,
                border: `1px solid ${GRAY_200}`,
                padding: '.6rem'
              }}
              onClick={onClose}
            >
              <Close fontSize="large" />
            </IconButton>
          </Stack>

          <Stack
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            gap="2.4rem"
            padding="4.8rem 2.4rem"
            sx={{
              height: 'calc(100% - 7.3rem)',
              boxSizing: 'border-box'
            }}
          >
            <Stack height="100%" flexDirection="row" alignItems="center">
              <IconButton
                size="small"
                sx={{
                  color: GRAY_800,
                  border: `1px solid ${GRAY_200}`,
                  padding: '.8rem'
                }}
                onClick={() => switchExpandedVisual(ExpandedVisualSwitchDirection.LEFT)}
              >
                <WestRoundedIcon fontSize="large" />
              </IconButton>
            </Stack>

            <ExpandedVisual
              signedUrl={signedUrl}
              isSelected={isSelected}
              isSelectable={isSelectable}
              isFavorite={isFavorite}
              isFavoriteSelectable={isFavoriteSelectable}
              visualMetadata={visualMetadata}
              switchExpandedVisualType={switchExpandedVisualType}
              expandedVisualType={expandedVisualType}
              onSelect={onSelect}
              onClickFavButton={() => toggleFavoriteOnImage?.(filename)}
            />

            {!filename &&
              <ErrorMessage className={styles.error} error_type={APIRequestErrorType.NOTFOUND_ERROR} />
            }

            <Stack height="100%" flexDirection="row" alignItems="center">
              <IconButton
                size="small"
                sx={{
                  color: GRAY_800,
                  border: `1px solid ${GRAY_200}`,
                  padding: '.8rem'
                }}
                onClick={() => switchExpandedVisual(ExpandedVisualSwitchDirection.RIGHT)}
              >
                <EastRoundedIcon fontSize="large" />
              </IconButton>
            </Stack>
          </Stack>
        </Modal>
      }
      <PhotoStudioUnsupportedModal
        isOpen={isPhotoStudioUnsupportedModalOpen}
        onClose={() => setIsPhotoStudioUnsupportedModalOpen(false)}
      />
    </Fragment>
  )
}
